import {Button, Col, Flex, Modal, Row, Empty, Segmented, Space, Tooltip, Image} from "antd";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {
    faArrowUpFromBracket,
    faFile,
    faGrip, faImage,
    faList, faMusic, faPlay,
    faStop
} from "@fortawesome/free-solid-svg-icons";
import {ViewMode} from "./ViewMode";
import React, {useEffect} from "react";
import {UploadButton} from "../../components/UploadButton";
import {useCookies} from "react-cookie";
import {useNavigate, useParams} from "react-router-dom";
import {useAppDispatch, useAppSelector} from "../../feature/tools";
import {StorageProvider} from "../../storage/providers/StorageProvider";
import {FileItem} from "../../storage/providers/FileItem";
import {openAccountSettings, openProjectSettings} from "../../feature/project/appSettingsSlice";
import {editorRoles, NavigateWithUserPermission} from "./WithUserPermission";

export const FilesBrowserModal = (props: {
    root: string,
    onDismiss: () => void,
    onSelect: (file: string) => void,
    types: string[],
}) => {
    const [file, setFile] = React.useState<string | null>(null)
    const {projectId} = useParams();

    return (
        <Modal
            maskClosable={false}
            destroyOnClose={true}
            open={true}
            title={'Files Browser'}
            okText={'Select'}
            okButtonProps={{disabled: !file}}
            onCancel={() => {
                props.onDismiss()
            }}
            onOk={() => props.onSelect(file ?? "")}
            width={1000}
        >
            <FilesBrowser root={props.root} types={props.types} onFileSelected={(f) => {
                if (projectId) {
                    setFile(f)
                }
            }}/>
        </Modal>
    )
}

export const FilesBrowser = (props: {
    root: string,
    onFileSelected?: (file: string) => void,
    types?: string[],
}) => {
    const dispatch = useAppDispatch()
    const storageProvider = useAppSelector(state => state.storageSlice.storageProvider)
    const projectStorageProvider = useAppSelector(state => state.storageSlice.projectStorageProviderSettings)
    const {projectId, storyId} = useParams()
    const [file, setFile] = React.useState<FileItem | null>(null)
    const [cookies, setCookie] = useCookies(['filesView'])
    const [files, setFiles] = React.useState<FileItem[]>([])
    const [type, setType] = React.useState<string | null>("images")
    const [audio, setAudio] = React.useState<HTMLAudioElement | null>(null)
    const navigate = useNavigate()

    useEffect(() => {
        if (storageProvider) {
            storageProvider.list(props.root)
                .then((data) => {
                    setFiles(data.filter(f => !f.name.endsWith("/")))
                })
        }
        return () => {
            audio?.pause()
            setAudio(null)
        };
    }, [storageProvider, props.root])

    useEffect(() => {
        if (audio) {
            audio.play()
        }
    }, [audio]);

    return (
        <div style={{height: "100%", minHeight: 600, width: '100%'}}>
            {projectStorageProvider && !storageProvider ?
                <Empty description={<>Please select a Storage Provider first in the <Button
                    size={'small'}
                    onClick={() => dispatch(openProjectSettings('storage'))}
                >Project Settings
                </Button></>}/> : <>
                    <Flex justify={'space-between'}>
                        <Segmented
                            options={[
                                {
                                    value: 'images',
                                    icon: <Tooltip title="Images"><FontAwesomeIcon
                                        icon={faImage}/></Tooltip>,
                                    title: "Images",
                                    disabled: !props.types?.includes('images') && (props.types?.length ?? 0) > 0
                                },
                                {
                                    value: 'audio',
                                    icon: <Tooltip title="Audio"><FontAwesomeIcon
                                        icon={faMusic}/></Tooltip>,
                                    title: "Audio",
                                    disabled: !props.types?.includes('audio') && (props.types?.length ?? 0) > 0
                                },
                                {
                                    value: 'documents',
                                    icon: <Tooltip title="Documents"><FontAwesomeIcon
                                        icon={faFile}/></Tooltip>,
                                    title: "Documents",
                                    disabled: !props.types?.includes('documents') && (props.types?.length ?? 0) > 0
                                },
                            ]}
                            onChange={(value) => {
                                setType(value)
                                setFile(null)
                            }}
                            value={type}
                        />
                        <Space>
                            <Tooltip title="Upload File">
                                <UploadButton
                                    path={props.root}
                                    style={{backgroundColor: 'rgba(255, 255, 255, 0.9)'}}
                                    title={'Upload'}
                                    icon={<FontAwesomeIcon icon={faArrowUpFromBracket}/>}
                                    onFileUploaded={() => {
                                        if (storageProvider) {
                                            storageProvider.list(
                                                props.root
                                            ).then((data) => {
                                                setFiles(data.filter(f => !f.name.endsWith("/")))
                                            })
                                        }
                                    }} onFileDeleted={() => {
                                }}/>
                            </Tooltip>
                            <Segmented
                                options={[
                                    {
                                        value: 'list',
                                        icon: <Tooltip title="List View"><FontAwesomeIcon
                                            icon={faList}/></Tooltip>
                                    },
                                    {
                                        value: 'grid',
                                        icon: <Tooltip title="Grid View"><FontAwesomeIcon
                                            icon={faGrip}/></Tooltip>
                                    },
                                ]}
                                onChange={(value) => setCookie('filesView', value)}
                                value={cookies['filesView'] || ViewMode.Grid}
                            />

                        </Space>
                    </Flex>
                    <div style={{height: 10}}></div>
                    <Row gutter={10} style={{minHeight: 558, height: "calc(100% - 80px)"}}>
                        <Col span={file ? 16 : 24} style={{minHeight: 558, height: "100%"}}
                             onClick={() => setFile(null)}>
                            <Flex justify={'center'} align={files.length > 0 ? 'start' : 'center'}
                                  style={{width: "100%", height: "100%"}}>
                                <Files
                                    files={files.filter((file) => {
                                        return type === 'documents' && isDocument(file.name) || type === 'audio' && isAudio(file.name) || type === 'images' && isImage(file.name)
                                    }).map(f => ({
                                        ...f,
                                        name: parseUrl(storageProvider, f.name.split('/').pop() ?? '', props.root)
                                    }))}
                                    file={file}
                                    viewMode={cookies['filesView'] === ViewMode.List ? ViewMode.List : ViewMode.Grid}
                                    onFileSelected={(file: FileItem) => {
                                        setFile(file)
                                        props.onFileSelected?.(file.name)
                                    }}/>
                            </Flex>
                        </Col>
                        {file ? <Col span={8} style={{height: "100%"}}>

                            <Space direction={'vertical'} style={{width: "100%"}}>
                                <Flex style={{
                                    width: "100%",
                                    aspectRatio: 1,
                                    borderRadius: '6px',
                                    background: "#aaa",
                                    overflow: 'hidden'
                                }} justify={'center'} align={'center'}>
                                    {isAudio(file.name) ?
                                        <Flex align={'center'} justify={'center'} style={{
                                            padding: 10,
                                            borderRadius: 100,
                                            background: "#eee",
                                            cursor: "pointer",
                                            width: 50,
                                            height: 50
                                        }}>
                                            <FontAwesomeIcon icon={audio ? faStop : faPlay} size={'2xl'}
                                                             onClick={() => {
                                                                 if (audio) {
                                                                     audio.pause()
                                                                     setAudio(null)
                                                                 } else {
                                                                     setAudio(new Audio(file.name))
                                                                 }
                                                             }}/>
                                        </Flex> :
                                        isImage(file.name) ?
                                            <Image
                                                alt={file.name}
                                                src={file ? file.name : '/images/placeholder.png'}
                                                style={{
                                                    width: "100%",
                                                    height: "100%",
                                                    objectFit: 'contain'
                                                }}
                                            /> :
                                            <Flex align={'center'} justify={'center'} style={{}}>
                                                <FontAwesomeIcon
                                                    icon={faFile}
                                                    size={'10x'}/>
                                            </Flex>
                                    }
                                </Flex>
                                <div
                                    style={{overflowWrap: 'anywhere'}}>{file.name.split('/').pop()}</div>
                                <div><b>Size:</b> {formatBytes(file.size)}</div>
                                <div><b>Modified:</b> {file.modified.toUTCString()}</div>
                                <Space>
                                    <Button type={'primary'} danger={true} onClick={() => {
                                        if (storageProvider) {
                                            storageProvider.delete(
                                                file.name.replace(storageProvider.baseUrl() + "/", '')
                                            ).then((url) => {
                                                audio?.pause()
                                                setAudio(null)
                                                setFile(null)
                                                storageProvider.list(
                                                    props.root
                                                ).then((data) => {
                                                    setFiles(data.filter(f => !f.name.endsWith("/")))
                                                })
                                            })
                                        }
                                    }}>Delete</Button>
                                    {file.name.endsWith('draft.json') ? <Button
                                        type={'primary'}
                                        onClick={() => navigate(`/projects/${projectId}/stories/${storyId}/story-crafter`)}>
                                        Open
                                        Draft
                                    </Button> : ''}
                                    <Button
                                        onClick={() => window.open(file.name, '_blank')?.focus()}>
                                        Open file
                                    </Button>
                                </Space>
                            </Space>
                        </Col> : ''}
                    </Row>
                </>}
        </div>
    )

}

const Files = (props: {
    files: FileItem[],
    file: FileItem | null,
    viewMode: ViewMode,
    onFileSelected: (file: FileItem) => void,
}) => {
    return (
        <div style={{width: "100%"}}>
            {props.files.length === 0 ? <Empty description={'No files found'}/> :
                (props.viewMode === ViewMode.List ?
                    <Row gutter={[16, 8]}>
                        {props.files.map((item: FileItem) => (
                            <FileListItem key={item.name} selected={item.name == props.file?.name} file={item}
                                          onSelected={props.onFileSelected}/>
                        ))}
                    </Row> :
                    <Row gutter={[16, 16]}>
                        {props.files.map((item: FileItem) => (
                            <FileGridItem hasSelected={props.file?.name != ""} key={item.name} file={item}
                                          selected={item.name == props.file?.name}
                                          onSelected={props.onFileSelected}/>
                        ))}
                    </Row>)
            }
        </div>
    )
}

export const FileListItem = (props: { file: FileItem, selected: boolean, onSelected: (file: FileItem) => void }) => {

    return (
        <Col span={24} key={props.file.name}>
            <Flex onClick={(e) => {
                props.onSelected(props.file)
                e.stopPropagation()
            }} align={"center"} gap={10}
                  className={"file-list-item " + (props.selected ? 'selected' : '')}>
                <Flex style={{width: 32, height: 32, borderRadius: '4px', background: "#aaa"}} justify={'center'}
                      align={'center'}>
                    {isAudio(props.file.name) ?
                        <FontAwesomeIcon icon={faMusic} size={'sm'}/> : isImage(props.file.name) ?
                            <img
                                alt={props.file.name}
                                src={props.file ? props.file.name : '/images/placeholder.png'}
                                style={{
                                    width: 32,
                                    height: 32,
                                    objectFit: 'contain'
                                }}
                            /> :
                            <FontAwesomeIcon icon={faFile} size={'sm'}/>}
                </Flex>
                <Flex justify={'space-between'} style={{width: "calc(100% - 42px)"}}>
                    <div style={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        textWrap: 'nowrap',
                        whiteSpace: "nowrap"
                    }}>{props.file.name.split('/').pop()}</div>
                    <div style={{textWrap: 'nowrap'}}>{formatBytes(props.file.size)}</div>
                </Flex>
            </Flex>
        </Col>
    )
}

export const FileGridItem = (props: {
    file: FileItem,
    hasSelected: boolean,
    selected: boolean,
    onSelected: (file: FileItem) => void
}) => {

    return (
        <Col span={props.hasSelected ? 6 : 4} key={props.file.name}>
            <Flex vertical={true} onClick={(e) => {
                props.onSelected(props.file)
                e.stopPropagation()
            }} align={"center"} gap={10}
                  className={"file-list-item " + (props.selected ? 'selected' : '')}>
                <Flex
                    style={{width: "100%", aspectRatio: 1, borderRadius: '6px', background: "#aaa", overflow: 'hidden'}}
                    align={'center'} justify={'center'}>
                    {isAudio(props.file.name) ?
                        <FontAwesomeIcon icon={faMusic} size={'2xl'}/> : isImage(props.file.name) ?
                            <img
                                alt={props.file.name}
                                src={props.file ? props.file.name : '/images/placeholder.png'}
                                style={{
                                    width: "100%",
                                    height: "100%",
                                    objectFit: 'contain'
                                }}
                            /> :
                            <FontAwesomeIcon icon={faFile} size={'2xl'}/>
                    }
                </Flex>
                <Flex justify={'space-between'} style={{width: "100%"}}>
                    <div style={{
                        width: "100%",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        textWrap: 'unset',
                        maxHeight: 20,
                        whiteSpace: "nowrap"
                    }}>{props.file.name.split('/').pop()}</div>
                    <div style={{textWrap: 'nowrap'}}>{formatBytes(props.file.size)}</div>
                </Flex>
            </Flex>
        </Col>
    )
}

export function parseUrl(storageProvider: StorageProvider | undefined, fileName: string, path: string): string {
    return storageProvider ? `${storageProvider.baseUrl()}/${path}${fileName}` : ''
}


function formatBytes(bytes: number, decimals = 2) {
    if (!+bytes) return '0 Bytes'

    const k = 1024
    const dm = decimals < 0 ? 0 : decimals
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']

    const i = Math.floor(Math.log(bytes) / Math.log(k))

    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`
}

export const isImage = (fileName: string) => {
    return imageFormats.includes(fileName.split(".").pop() ?? '')
}

export const isAudio = (fileName: string) => {
    return audioFormats.includes(fileName.split(".").pop() ?? '')
}

export const isDocument = (fileName: string) => {
    return !isImage(fileName) && !isAudio(fileName)
}

export const audioFormats = [
    'mp3',
    'wav',
]


export const imageFormats = [
    'jpg',
    'jpeg',
    'png',
    'gif',
    'bmp',
    'tiff',
    'webp',
    'svg',
    'avif',
]